import * as React from 'react';
import {
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
} from '@mui/material';
import CachedIcon from '@mui/icons-material/Cached';
import { IForm, IPromptCard } from '@homeporter/types';

type ICardType = IPromptCard | IForm | /* add other card unions */ any;

interface Props {
  card: ICardType;
  index: number;
  styles: any;
  documentSection: any; // your concrete type if available
  regenerateIndex: number | undefined;
  setSelectedCard: (v: { index: number; obj: ICardType }) => void;
  setRegenerateIndex: (i: number) => void;
  setRepaint: (v: boolean) => void;
  repaint: boolean;
}

export function RegenerateModalButton({
  card,
  index,
  styles,
  documentSection,
  regenerateIndex,
  setSelectedCard,
  setRegenerateIndex,
  setRepaint,
  repaint,
}: Props) {
  const [open, setOpen] = React.useState(false);

  const disabled = regenerateIndex !== undefined || card.validated;

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;
    setOpen(true);
  };

  const handleClose = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setOpen(false);
  };

  const handleConfirm = (e: React.MouseEvent) => {
    e.stopPropagation();

    // --- replicate your previous logic, minus window.confirm ---
    if (card.type === 'Prompt') {
      const prompt = documentSection.cards[index] as IPromptCard;
      // If you prefer immutability, replace these with a setState + immer produce
      prompt.modified = '';
      prompt.redo = [];
      prompt.undo = [];
      prompt.response = '';
    }

    card.score = undefined;
    setSelectedCard({ index, obj: card });
    setRegenerateIndex(index);
    setRepaint(!repaint);

    setOpen(false);
  };

  return (
    <>
      <IconButton
        onClick={handleOpen}
        disabled={disabled}
        sx={styles.viewButton}>
        <CachedIcon
          sx={{ color: card.validated ? '#eee' : 'rgba(0, 0, 0, 0.54)' }}
        />
      </IconButton>

      <Dialog
        open={open}
        onClose={(e: React.MouseEvent) => handleClose(e)}
        aria-labelledby="regenerate-title"
        onClick={(e) => e.stopPropagation()} // keep row clicks from firing
      >
        <DialogTitle id="regenerate-title">Regenerate response?</DialogTitle>

        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            {card.type === 'Prompt' ? (
              <Typography variant="body2">
                This will <b>reset the prompt</b> (clear response, undo/redo and
                modified state) and start a fresh generation.
              </Typography>
            ) : (
              <Typography variant="body2">
                This will reset your response and start a fresh generation.
              </Typography>
            )}
            {card.validated && (
              <Typography variant="caption" color="text.secondary">
                Note: This card is validated, so regeneration is disabled.
              </Typography>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            color="primary"
            autoFocus>
            Regenerate
          </Button>
          {/* If you want a spinner while some async work happens:
          <LoadingButton onClick={handleConfirm} variant="contained" loading>
            Regenerate
          </LoadingButton> */}
        </DialogActions>
      </Dialog>
    </>
  );
}
